/*
 * AI Recommendations model (CONTRACT §3.2, §4, §6). Pure: no React, no network.
 *
 * The server (careiq.n3.1 / n3.2) sends the merged, deduplicated list in display
 * order; the UI filters it and never re-sorts. The same helpers patch the
 * body with the state POST's updated[] (optimistically first) and build the
 * request bodies. recsFromLegacyTodo() is the client fallback for the old
 * kg/todo shape (the GET failed): identical guideline keywords group, nothing
 * else is inferred, and no review state exists.
 */
import { mapOrderType, normOrder, orderKey } from "./orderKey";

// The bodies this UI reads: n3.1, n3.2 (n3.1 plus each recommendation's
// `guideline` / `evidence` / `evidence_more`, SOURCE-LINKS-SURVEY §2.3), n3.3
// (n3.2 plus `headline` / `headline_source` / `detail`, CONTRACT §19) and n3.4
// (n3.3 plus the order ledger's fields: `action.display`, `action_verb`,
// `order_shared_with`, `next_step`, `no_order_reason`; owner 10-07, V4 "order
// ledger"). An unknown schema is treated as a failed GET (the legacy fallback),
// never rendered half-understood. A server that does not know the schema it is
// asked for answers n3.1 to it; the tab then asks again one step down (n3.4 ->
// n3.3 -> n3.2) until it is served what it asked for or has asked for n3.2.
export const SCHEMAS = ["careiq.n3.1", "careiq.n3.2", "careiq.n3.3", "careiq.n3.4"];
export const SCHEMA = SCHEMAS[SCHEMAS.length - 1];
export const SCHEMA_N32 = "careiq.n3.2";
export const knownSchema = (s) => SCHEMAS.includes(s);
// The schema to ask for again when the server answered `served` to `asked` (default SCHEMA; null: none).
export const schemaRetry = (served, asked = SCHEMA) => {
  if (served !== "careiq.n3.1" || asked === served) return null;
  const i = SCHEMAS.indexOf(asked);
  return i > SCHEMAS.indexOf(SCHEMA_N32) ? SCHEMAS[i - 1] : null;
};

export const PRIORITY_LABEL = { critical: "Critical", overdue: "Overdue", due: "Due", consider: "Consider", risk: "Risk" };
export const PRIORITY_RANK = { critical: 0, overdue: 1, due: 2, consider: 3, risk: 4 };
export const SOURCE_LABEL = {
  care_gap: "care gap", suggestion: "recommendation", risk_factor: "risk factor", followup_ai: "follow-up reminder",
  critical_row: "critical alert", rpm: "RPM", suggested_order: "suggested order",
};
const SOURCE_PLURAL = {
  care_gap: "care gaps", suggestion: "recommendations", risk_factor: "risk factors", followup_ai: "follow-up reminders",
  critical_row: "critical alerts", rpm: "RPM", suggested_order: "suggested orders",
};
const MERGED_ORDER = ["care_gap", "suggestion", "risk_factor", "followup_ai", "critical_row", "rpm", "suggested_order"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/* ───────── dates and times (practice-local strings; never re-zoned) ───────── */

// "2026-09-11" -> "Sep 11" (", 2025" only when the year differs from refYear).
export const shortDate = (ymd, refYear) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ""));
  if (!m) return "";
  const s = `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}`;
  return refYear && Number(m[1]) !== Number(refYear) ? `${s}, ${m[1]}` : s;
};

// "2026-10-03T10:41:00-05:00" -> "10:41 AM" (the clock part as written: the
// server sends practice-local times, so the browser's zone never shifts it).
export const clockTime = (iso) => {
  const m = /T(\d{2}):(\d{2})/.exec(String(iso || ""));
  if (!m) return "";
  const h = Number(m[1]);
  return `${h % 12 || 12}:${m[2]} ${h >= 12 ? "PM" : "AM"}`;
};

export const isoDay = (iso) => {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(iso || ""));
  return m ? m[1] : "";
};

// The browser's calendar day as YYYY-MM-DD.
export const localToday = (now = new Date()) =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

// "CareIQ ran today at 6:10 AM" / "CareIQ ran Sep 30" / "CareIQ has not run for this patient".
export const ranLine = (meta, today = localToday()) => {
  const g = meta && meta.generated_at;
  if (!g) {
    if (meta && meta.todo_file === "unreadable") return "CareIQ results could not be read";
    // The legacy kg/todo fallback has a list but no run time: say nothing
    // rather than "has not run" under the items it shows.
    if (meta && meta.todo_file === "ok") return "";
    return "CareIQ has not run for this patient";
  }
  const day = isoDay(g);
  if (day === today) return `CareIQ ran today at ${clockTime(g)}`;
  return `CareIQ ran ${shortDate(day, today.slice(0, 4))}`;
};

// "Merged from 14 items — 6 care gaps, 5 recommendations, 3 risk factors · CareIQ ran today at 6:10 AM"
export const mergedFromLine = (meta, today) => {
  const mf = (meta && meta.merged_from) || {};
  const parts = MERGED_ORDER.filter((k) => Number(mf[k]) > 0).map((k) => `${mf[k]} ${SOURCE_PLURAL[k]}`);
  const ran = ranLine(meta, today);
  if (!Number(mf.total)) return ran;
  return `Merged from ${mf.total} items — ${parts.join(", ")}${ran ? ` · ${ran}` : ""}`;
};

/* ───────── one item ───────── */

// Provenance segments: [priority, sources, when] (the priority is red for critical).
export const provenanceParts = (rec) => {
  const seen = [];
  ((rec && rec.provenance && rec.provenance.sources) || []).forEach((s) => {
    const label = SOURCE_LABEL[s.list] || s.list;
    if (label && !seen.includes(label)) seen.push(label);
  });
  return {
    priority: PRIORITY_LABEL[rec && rec.priority] || cap(String((rec && rec.priority) || "")),
    critical: rec && rec.priority === "critical",
    sources: cap(seen.join(" + ")),
    when: (rec && rec.provenance && rec.provenance.when) || "",
  };
};

export const provenanceText = (rec) => {
  const p = provenanceParts(rec);
  return [p.priority, p.sources, p.when].filter(Boolean).join(" · ");
};

/* ───────── lists ───────── */

export const recs = (body) => (body && Array.isArray(body.recommendations) ? body.recommendations : []);
export const openRecs = (body) => recs(body).filter((r) => r.status === "open");

// Reviewed at THIS visit (shown as "Reviewed today (N) · Undo"); an item
// reviewed at an earlier visit with unchanged evidence is hidden (D8).
export const reviewedThisVisit = (body) =>
  recs(body)
    .filter((r) => r.status === "reviewed" && r.reviewed && r.reviewed.this_visit)
    .sort((a, b) => String(a.reviewed.at).localeCompare(String(b.reviewed.at)));

export const hiddenEarlierVisit = (body) =>
  recs(body).filter((r) => r.status === "reviewed" && r.reviewed && !r.reviewed.this_visit);

export const stateAvailable = (body) => Boolean(body && body.meta && body.meta.state === "ok");


/* ───────── state POST (CONTRACT §6) ───────── */

export const kindOfKey = (key) => {
  const p = String(key || "").split(":")[0];
  return { rec: "rec", gap: "rec", lab: "fu_lab", doc: "fu_doc", plan: "fu_plan", order: "fu_order", med: "fu_med" }[p] || null;
};

// One change for an item (a recommendation or a Follow up row).
export const buildChange = (item, status, { appointmentid = null, orderEvent = null, textSnapshot = null, cascade = true } = {}) => {
  const key = item.issue_key || item.key;
  const change = {
    key,
    kind: kindOfKey(key),
    status,
    evidence_fp: item.evidence_fp,
    appointmentid: appointmentid == null || appointmentid === "" ? null : Number(appointmentid),
  };
  if (textSnapshot) change.text_snapshot = String(textSnapshot).slice(0, 1000);
  if (cascade && item.key && Array.isArray(item.folded) && item.folded.length) {
    change.cascade = item.folded.map((f) => ({ key: f.issue_key, evidence_fp: f.evidence_fp }));
  }
  if (orderEvent) change.order_event = orderEvent;
  return change;
};

// What the server will answer, for the optimistic update.
export const optimisticUpdated = (changes, { by = "", at = null, appointmentid = null } = {}) => {
  const out = [];
  const reviewed = (c, via, orderId) => ({
    at: at || new Date().toISOString(), by, via, order_id: orderId == null ? null : orderId,
    appointmentid: appointmentid == null ? null : Number(appointmentid), this_visit: true,
  });
  (changes || []).forEach((c) => {
    const added = c.order_event && c.order_event.type === "added" ? c.order_event.order_id : null;
    const via = added && c.kind === "rec" ? "order" : "manual";
    out.push({
      key: c.key, kind: c.kind, status: c.status, evidence_fp: c.evidence_fp,
      reviewed: c.status === "reviewed" ? reviewed(c, via, via === "order" ? added : null) : null,
      cascade_from: null, added_order_id: added,
    });
    (c.cascade || []).forEach((k) => out.push({
      key: k.key, kind: "rec", status: c.status, evidence_fp: k.evidence_fp,
      reviewed: c.status === "reviewed" ? reviewed(c, "manual", null) : null,
      cascade_from: c.key, added_order_id: null,
    }));
  });
  return out;
};

// Apply updated[] (optimistic or the server's) to a GET body; returns a new body.
export const applyStateUpdates = (body, updated) => {
  if (!body || !Array.isArray(updated) || !updated.length) return body;
  const byKey = new Map(updated.map((u) => [u.key, u]));
  const patch = (item, key) => {
    const u = byKey.get(key);
    if (!u) return item;
    const next = { ...item, status: u.status, reviewed: u.status === "reviewed" ? u.reviewed : null };
    if (u.status === "open") { next.reopened = false; next.reopen_reason = null; }
    if (u.evidence_fp) next.evidence_fp = u.evidence_fp;
    return next;
  };
  return {
    ...body,
    recommendations: recs(body).map((r) => patch(r, r.issue_key)),
    followup: body.followup
      ? { ...body.followup, rows: (body.followup.rows || []).map((r) => (r.display === "row" ? patch(r, r.key) : r)) }
      : body.followup,
  };
};

// Set (or clear) an action's existing_order on every item whose action has this order key.
export const setExistingOrder = (body, key, existing) => {
  if (!body || !key) return body;
  const fix = (item) => (item && item.action && item.action.order_key === key
    ? { ...item, action: { ...item.action, existing_order: existing } } : item);
  return {
    ...body,
    recommendations: recs(body).map(fix),
    followup: body.followup ? { ...body.followup, rows: (body.followup.rows || []).map(fix) } : body.followup,
    suggested_orders: (body.suggested_orders || []).map((s) => (s.order_key === key ? { ...s, existing_order: existing } : s)),
  };
};

// Clear every existing_order whose order is gone (isGone(order_id) true):
// the order was deleted after the GET. Returns the same body when nothing
// changed.
export const clearGoneOrders = (body, isGone) => {
  if (!body || typeof isGone !== "function") return body;
  let changed = false;
  const fix = (item) => {
    const ex = item && item.action && item.action.existing_order;
    if (!ex || !isGone(ex.order_id)) return item;
    changed = true;
    return { ...item, action: { ...item.action, existing_order: null } };
  };
  const next = {
    ...body,
    recommendations: recs(body).map(fix),
    followup: body.followup ? { ...body.followup, rows: (body.followup.rows || []).map(fix) } : body.followup,
    suggested_orders: (body.suggested_orders || []).map((sug) => {
      if (!sug || !sug.existing_order || !isGone(sug.existing_order.order_id)) return sug;
      changed = true;
      return { ...sug, existing_order: null };
    }),
  };
  return changed ? next : body;
};

// Every order key some existing_order already covers (for isAddedOrder).
export const existingOrderKeys = (body) => {
  const keys = new Set();
  const take = (a) => { if (a && a.existing_order && a.order_key) keys.add(a.order_key); };
  recs(body).forEach((r) => take(r.action));
  ((body && body.followup && body.followup.rows) || []).forEach((r) => take(r.action));
  ((body && body.suggested_orders) || []).forEach((s) => { if (s.existing_order) keys.add(s.order_key); });
  return keys;
};

// The confirmation shown in place of an item's button once its order exists.
//   added here, before recording   -> "Added to this visit's orders" (Undo)
//   added here, while recording     -> "Added to Current Orders" (Undo)
//   added from the Suggested card   -> "Added from Suggested Orders" (Undo)
//   on the visit, not added here    -> "✓ In this visit's orders" (no Undo)
export const addedText = ({ from, pre, addedHere }) => {
  if (!addedHere) return "✓ In this visit’s orders";
  if (from === "live") return "Added from Suggested Orders";
  return pre ? "Added to this visit’s orders" : "Added to Current Orders";
};

/* ───────── legacy kg/todo fallback (the GET failed) ───────── */

const gapPriority = (type) => {
  const t = String(type || "").toLowerCase();
  if (t === "urgent" || t === "critical") return "critical";
  if (t === "overdue") return "overdue";
  if (t === "due") return "due";
  return "due";
};

// Old v1 kg/todo payload -> a careiq.n3.1-shaped body with no state, no
// Follow up rows and client-grouped recommendations (identical guideline
// keywords only). Orders: each todo suggested order becomes a "Suggested
// order" row (D10), keyed with the fallback aliases.
export const recsFromLegacyTodo = (todo, { aliases = {}, dos = null } = {}) => {
  const ins = (todo && (todo.patient_insights || todo)) || {};
  const ci = ins.care_intelligence || {};
  const items = [];
  // A todo `headline` is NOT read here: only the v2 GET serves one, after its guard (CONTRACT §19, the
  // trust boundary); the fallback shows the sentence, clipped at a clause like any item without one.
  (ci.care_gaps || []).forEach((g) => items.push({ list: "care_gap", text: g.text || g.description || g.recommendation || "", type: g.type || null, priority: gapPriority(g.type), kw: g.guideline_reference && g.guideline_reference.search_label_keywords, src: g.guideline_reference }));
  (ci.suggestions || []).forEach((s) => items.push({ list: "suggestion", text: s.recommendation || s.description || "", type: null, priority: "consider", kw: s.guideline_reference && s.guideline_reference.search_label_keywords, src: s.guideline_reference }));
  ((ins.risk_radar && ins.risk_radar.risk_factors) || []).forEach((r) => items.push({ list: "risk_factor", text: r.risk || r.area || r.description || "", type: null, priority: "risk", kw: r.guideline_reference && r.guideline_reference.search_label_keywords, src: r.guideline_reference }));
  const groups = [];
  const byKw = new Map();
  items.filter((i) => i.text).forEach((i, n) => {
    const kw = normOrder(i.kw || "");
    const g = kw && byKw.get(kw);
    if (g) { g.items.push(i); return; }
    const ng = { id: `legacy:${n}`, items: [i] };
    if (kw) byKw.set(kw, ng);
    groups.push(ng);
  });
  const recommendations = groups.map((g) => {
    const lead = g.items.reduce((a, b) => (PRIORITY_RANK[b.priority] < PRIORITY_RANK[a.priority] ? b : a));
    return {
      issue_key: `rec:${g.id.replace(/[^a-z0-9:._-]/gi, "-").toLowerCase()}`, kind: "rec", origin: "merged",
      text: lead.text, priority: lead.priority,
      provenance: { sources: g.items.map((i) => ({ list: i.list, item_key: "", text: i.text, type: i.type, guideline: i.src ? { source: i.src.source || null, keywords: i.src.search_label_keywords || null, link: i.src.link || null } : null })), open_since: null, visits: null, last_done: null, due_since: null, when: lead.src && lead.src.source ? lead.src.source : null },
      action: null, status: "open", reopened: false, reopen_reason: null, evidence_fp: null, reviewed: null,
    };
  }).sort((a, b) => PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]);
  (ci.suggested_orders || []).forEach((o) => {
    const name = String(o.order || o.description || "").replace(/^Order\s+/i, "").trim();
    const key = orderKey(name, aliases);
    if (!key || recommendations.some((r) => r.action && r.action.order_key === key)) return;
    recommendations.push({
      issue_key: `rec:order:${key}`.toLowerCase().replace(/[^a-z0-9:._-]/g, "-"), kind: "rec", origin: "suggested_order",
      text: name, priority: "consider",
      provenance: { sources: [{ list: "suggested_order", item_key: "", text: o.order || name, type: null, guideline: null }], open_since: null, visits: null, last_done: null, due_since: null, when: "CareIQ suggested order" },
      action: { label: `Add ${name.length > 24 ? "order" : name}`, ordername: name, ordertype: mapOrderType(o.order_type || o.ordertype, name), order_key: key, catalogue_name: null, lookup_id: null, why: o.detail || null, existing_order: null },
      status: "open", reopened: false, reopen_reason: null, evidence_fp: null, reviewed: null,
    });
  });
  const total = items.length;
  const count = (l) => items.filter((i) => i.list === l).length;
  return {
    schema: "legacy-fallback",
    meta: {
      generated_at: null, todo_file: "ok", last_run: null,
      visit: { dos, appointmentid: null, dos_source: "param" },
      documents: { list_missing: false, list_fetched_at: null, visit_day_stale: false, imaging_pending_ai: 0 },
      order_aliases: aliases, state: "unavailable", state_reason: "read_error",
      merged_from: { total, care_gap: count("care_gap"), suggestion: count("suggestion"), risk_factor: count("risk_factor"), followup_ai: 0, critical_row: 0, rpm: 0, suggested_order: 0 },
      critical: { tiers: [], panic_table: { version: null, status: "draft" } }, computed_ms: 0,
    },
    recommendations,
    followup: { last_visit: null, window: null, rows: [] },
    suggested_orders: [],
    document_labels: (todo && (todo.document_labels || (todo.patient_insights && todo.patient_insights.document_labels))) || [],
  };
};
